import type { QuizAttempt } from "@/api";

/**
 * Formats the last attempt date as a relative time (e.g., "5 minutes ago", "2 hours ago", "7 days ago")
 * if it's within the last week, or as a formatted date (e.g., "5 July 2024")
 * if it's older than a week.
 *
 * If the attempt was made today (within the last 24 hours), it will show:
 * - "Just now" if less than a minute ago
 * - "X minutes ago" if less than an hour ago
 * - "X hours ago" if less than 12 hours ago
 * - "Today" if 12 hours ago or more but still today
 *
 * @returns A formatted string representing when the last attempt was made
 * @param attempt
 */
export function getAttemptDate(attempt: QuizAttempt): string {
  const attemptDate = new Date(attempt.completedAt ?? 0);
  const currentDate = new Date();

  // Calculate the difference in milliseconds
  const diffTime = currentDate.getTime() - attemptDate.getTime();

  // Calculate different time units
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // If less than a week ago, show relative time
  if (diffDays < 7) {
    // If hours is greater than or equal to 12 but still today
    if (diffHours >= 12 && diffDays === 0) {
      return "Today";
    }
    // Less than an hour
    else if (diffMinutes < 60) {
      if (diffMinutes === 0) {
        return "Just now";
      } else if (diffMinutes === 1) {
        return "1 minute ago";
      } else {
        return `${diffMinutes} minutes ago`;
      }
    }
    // Less than 12 hours
    else if (diffHours < 12) {
      if (diffHours === 1) {
        return "1 hour ago";
      } else {
        return `${diffHours} hours ago`;
      }
    }
    // Days
    else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays > 1) {
      return `${diffDays} days ago`;
    }
  }

  // If more than a week ago, format as date
  return attemptDate.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Returns the shortcut for a given key
 * @param key - The key to get the shortcut for
 * @returns The shortcut for the given key
 */
export function getShortcut(key: number) {
  return String.fromCharCode(65 + key);
}
